// Supported locales
export const SUPPORTED_LOCALES = ['es', 'de', 'fr', 'it', 'pt', 'en'] as const;
export type Locale = typeof SUPPORTED_LOCALES[number];

export const DEFAULT_LOCALE: Locale = 'es';

// Locale metadata
export interface LocaleConfig {
  code: Locale;
  name: string;
  nativeName: string;
  flag: string;
  dir: 'ltr' | 'rtl';
  hreflang: string;
  // Audience personalization
  audienceKey: string; // e.g., "latinos", "italians", "germans"
  trustMessage: string; // e.g., "Más de 10 años ayudando a italianos en Suiza"
}

export const LOCALE_CONFIGS: Record<Locale, LocaleConfig> = {
  es: {
    code: 'es',
    name: 'Español',
    nativeName: 'Español',
    flag: '🇪🇸',
    dir: 'ltr',
    hreflang: 'es',
    audienceKey: 'hispanohablantes',
    trustMessage: 'Más de 12 años ayudando a hispanohablantes en Suiza',
  },
  de: {
    code: 'de',
    name: 'Alemán',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    dir: 'ltr',
    hreflang: 'de',
    audienceKey: 'deutschsprachige',
    trustMessage: 'Seit über 12 Jahren helfen wir Deutschsprachigen in der Schweiz',
  },
  fr: {
    code: 'fr',
    name: 'Francés',
    nativeName: 'Français',
    flag: '🇫🇷',
    dir: 'ltr',
    hreflang: 'fr',
    audienceKey: 'francophones',
    trustMessage: 'Plus de 12 ans d\'aide aux francophones en Suisse',
  },
  it: {
    code: 'it',
    name: 'Italiano',
    nativeName: 'Italiano',
    flag: '🇮🇹',
    dir: 'ltr',
    hreflang: 'it',
    audienceKey: 'italiani',
    trustMessage: 'Oltre 12 anni di assistenza agli italiani in Svizzera',
  },
  pt: {
    code: 'pt',
    name: 'Portugués',
    nativeName: 'Português',
    flag: '🇵🇹',
    dir: 'ltr',
    hreflang: 'pt',
    audienceKey: 'lusófonos',
    trustMessage: 'Mais de 12 anos ajudando lusófonos na Suíça',
  },
  en: {
    code: 'en',
    name: 'Inglés',
    nativeName: 'English',
    flag: '🇬🇧',
    dir: 'ltr',
    hreflang: 'en',
    audienceKey: 'english-speakers',
    trustMessage: 'Over 12 years helping English speakers in Switzerland',
  },
};

// Route path translations per locale
export const ROUTE_TRANSLATIONS: Record<Locale, Record<string, string>> = {
  es: {
    services: 'servicios',
    about: 'sobre-nosotros',
    contact: 'contacto',
    blog: 'blog',
    zones: 'zonas',
    insurance: 'consultoria-seguros',
    legal: 'asesoria-legal',
    languages: 'cursos-idiomas',
    admin: 'gestoria-administracion',
    relocation: 'pack-relocation',
    vehicles: 'alquileres-vehiculos',
    housing: 'alquileres-viviendas',
    digital: 'soluciones-digitales-avanzadas',
    policies: 'politicas',
  },
  de: {
    services: 'dienstleistungen',
    about: 'ueber-uns',
    contact: 'kontakt',
    blog: 'blog',
    zones: 'standorte',
    insurance: 'versicherungsberatung',
    legal: 'rechtsberatung',
    languages: 'sprachkurse',
    admin: 'verwaltung',
    relocation: 'umzugspaket',
    vehicles: 'fahrzeugvermietung',
    housing: 'wohnungsvermietung',
    digital: 'digitale-loesungen',
    policies: 'rechtliches',
  },
  fr: {
    services: 'services',
    about: 'a-propos',
    contact: 'contact',
    blog: 'blog',
    zones: 'zones',
    insurance: 'conseil-assurances',
    legal: 'conseil-juridique',
    languages: 'cours-langues',
    admin: 'gestion-administrative',
    relocation: 'pack-relocation',
    vehicles: 'location-vehicules',
    housing: 'location-logements',
    digital: 'solutions-digitales',
    policies: 'mentions-legales',
  },
  it: {
    services: 'servizi',
    about: 'chi-siamo',
    contact: 'contatti',
    blog: 'blog',
    zones: 'zone',
    insurance: 'consulenza-assicurazioni',
    legal: 'consulenza-legale',
    languages: 'corsi-lingue',
    admin: 'gestione-amministrativa',
    relocation: 'pacchetto-relocation',
    vehicles: 'noleggio-veicoli',
    housing: 'affitto-alloggi',
    digital: 'soluzioni-digitali',
    policies: 'note-legali',
  },
  pt: {
    services: 'servicos',
    about: 'sobre-nos',
    contact: 'contacto',
    blog: 'blog',
    zones: 'zonas',
    insurance: 'consultoria-seguros',
    legal: 'assessoria-juridica',
    languages: 'cursos-idiomas',
    admin: 'gestao-administrativa',
    relocation: 'pacote-relocation',
    vehicles: 'aluguel-veiculos',
    housing: 'aluguel-habitacao',
    digital: 'solucoes-digitais',
    policies: 'politicas',
  },
  en: {
    services: 'services',
    about: 'about-us',
    contact: 'contact',
    blog: 'blog',
    zones: 'zones',
    insurance: 'insurance-consulting',
    legal: 'legal-advice',
    languages: 'language-courses',
    admin: 'administration',
    relocation: 'relocation-pack',
    vehicles: 'vehicle-rental',
    housing: 'housing-rental',
    digital: 'digital-solutions',
    policies: 'policies',
  },
};

// Helper to check if a locale is valid
export function isValidLocale(locale: string): locale is Locale {
  return SUPPORTED_LOCALES.includes(locale as Locale);
}

// Helper to get locale from URL path
export function getLocaleFromPath(pathname: string): Locale | null {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];
  if (first && isValidLocale(first)) {
    return first;
  }
  return null;
}

// Helper to build localized path
export function localizedPath(locale: Locale, path: string): string {
  // Remove any existing locale prefix
  const cleanPath = path.replace(/^\/(es|de|fr|it|pt|en)(\/|$)/, '/');
  if (locale === DEFAULT_LOCALE) {
    return cleanPath || '/';
  }
  return `/${locale}${cleanPath === '/' ? '' : cleanPath}`;
}
